import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    DATABASE_URL: z.string().url(),
    DATABASE_CA: z.string().optional(),
    NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
    BETTER_AUTH_SECRET: z.string(),

    GOOGLE_CLIENT_ID: z.string(),
    GOOGLE_CLIENT_SECRET: z.string(),
    LINKEDIN_CLIENT_ID: z.string(),
    LINKEDIN_CLIENT_SECRET: z.string(),
    MICROSOFT_CLIENT_ID: z.string(),
    MICROSOFT_CLIENT_SECRET: z.string(),
    R2_BUCKET_NAME: z.string(),
    R2_ACCESS_KEY_ID: z.string(),
    R2_SECRET_ACCESS_KEY: z.string(),
    R2_ENDPOINT: z.string(),
    R2_PUBLIC_URL: z.string(),
    // Messaging
    KLAVIYO_PRIVATE_KEY: z.string().optional(),
    KLAVIYO_LIST_ID: z.string().optional(),
    KLAVIYO_REVISION: z.string().default('2026-01-15'),

    // SMTP Email
    SMTP_HOST: z.string(),
    SMTP_PORT: z.string(),
    SMTP_USER: z.string(),
    SMTP_PASS: z.string(),
    // Stripe
    STRIPE_SECRET_KEY: z.string(),
    STRIPE_WEBHOOK_SECRET: z.string(),
    STRIPE_WEBHOOK_SECRET_TEST: z.string().optional(),
    // Admin Configuration
    ADMIN_EMAILS: z.string().optional().default(''),
    // Cron Security
    CRON_SECRET: z.string(),
    // AI Services
    KIE_API_KEY: z.string().optional(),
    KIE_WEBHOOK_KEY: z.string().optional(),
    OPENAI_API_KEY: z.string().optional(),
    // Soketi / Pusher
    SOKETI_APP_ID: z.string().default('app-id'),
    SOKETI_APP_SECRET: z.string().default('app-secret'),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    // NEXT_PUBLIC_CLIENTVAR: z.string(),
    NEXT_PUBLIC_APP_URL: z.string().url(),
    NEXT_PUBLIC_GA4_ID: z.string().optional(),
    NEXT_PUBLIC_GOOGLE_ADS_ID: z.string().optional(),
    NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_LABEL: z.string().optional(),
    NEXT_PUBLIC_GOOGLE_ADS_PURCHASE_LABEL: z.string().optional(),
    NEXT_PUBLIC_FACEBOOK_PIXEL_ID: z.string().optional(),
    NEXT_PUBLIC_KLAVIYO_PUBLIC_KEY: z.string().optional(),
    NEXT_PUBLIC_STRIPE_API_VERSION: z.string().default('2025-12-15.clover'),
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string(),
    NEXT_PUBLIC_GOOGLE_CLIENT_ID: z.string().optional(),
    // Soketi / Pusher (Public)
    NEXT_PUBLIC_SOKETI_APP_KEY: z.string().default('app-key'),
    NEXT_PUBLIC_SOKETI_HOST: z.string().default('soketi.zappingai.com'),
    NEXT_PUBLIC_SOKETI_PORT: z.string().default('443'),
    NEXT_PUBLIC_SOKETI_USE_TLS: z.string().default('true'),
    NEXT_PUBLIC_CLARITY_ID: z.string().optional(),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    DATABASE_URL: process.env.DATABASE_URL,
    DATABASE_CA: process.env.DATABASE_CA,
    NODE_ENV: process.env.NODE_ENV,
    BETTER_AUTH_SECRET: process.env.BETTER_AUTH_SECRET,

    GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID,
    GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET,
    LINKEDIN_CLIENT_ID: process.env.LINKEDIN_CLIENT_ID,
    LINKEDIN_CLIENT_SECRET: process.env.LINKEDIN_CLIENT_SECRET,
    MICROSOFT_CLIENT_ID: process.env.MICROSOFT_CLIENT_ID,
    MICROSOFT_CLIENT_SECRET: process.env.MICROSOFT_CLIENT_SECRET,
    R2_BUCKET_NAME: process.env.R2_BUCKET_NAME,
    R2_ACCESS_KEY_ID: process.env.R2_ACCESS_KEY_ID,
    R2_SECRET_ACCESS_KEY: process.env.R2_SECRET_ACCESS_KEY,
    R2_ENDPOINT: process.env.R2_ENDPOINT,
    R2_PUBLIC_URL: process.env.R2_PUBLIC_URL,
    // Messaging
    KLAVIYO_PRIVATE_KEY: process.env.KLAVIYO_PRIVATE_KEY,
    KLAVIYO_LIST_ID: process.env.KLAVIYO_LIST_ID,
    KLAVIYO_REVISION: process.env.KLAVIYO_REVISION,
    // SMTP Email
    SMTP_HOST: process.env.SMTP_HOST,
    SMTP_PORT: process.env.SMTP_PORT,
    SMTP_USER: process.env.SMTP_USER,
    SMTP_PASS: process.env.SMTP_PASS,
    // Stripe
    STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY,
    STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET,
    STRIPE_WEBHOOK_SECRET_TEST: process.env.STRIPE_WEBHOOK_SECRET_TEST,
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
    NEXT_PUBLIC_GA4_ID: process.env.NEXT_PUBLIC_GA4_ID,
    NEXT_PUBLIC_GOOGLE_ADS_ID: process.env.NEXT_PUBLIC_GOOGLE_ADS_ID,
    NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_LABEL: process.env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_LABEL,
    NEXT_PUBLIC_GOOGLE_ADS_PURCHASE_LABEL: process.env.NEXT_PUBLIC_GOOGLE_ADS_PURCHASE_LABEL,
    NEXT_PUBLIC_FACEBOOK_PIXEL_ID: process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID,
    NEXT_PUBLIC_KLAVIYO_PUBLIC_KEY: process.env.NEXT_PUBLIC_KLAVIYO_PUBLIC_KEY,
    NEXT_PUBLIC_STRIPE_API_VERSION: process.env.NEXT_PUBLIC_STRIPE_API_VERSION,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    NEXT_PUBLIC_GOOGLE_CLIENT_ID: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
    // Admin Configuration
    ADMIN_EMAILS: process.env.ADMIN_EMAILS,
    // Cron Security
    CRON_SECRET: process.env.CRON_SECRET,
    // AI Services
    KIE_API_KEY: process.env.KIE_API_KEY,
    KIE_WEBHOOK_KEY: process.env.KIE_WEBHOOK_KEY,
    OPENAI_API_KEY: process.env.OPENAI_API_KEY,
    // Soketi / Pusher
    SOKETI_APP_ID: process.env.SOKETI_APP_ID,
    SOKETI_APP_SECRET: process.env.SOKETI_APP_SECRET,
    NEXT_PUBLIC_SOKETI_APP_KEY: process.env.NEXT_PUBLIC_SOKETI_APP_KEY,
    NEXT_PUBLIC_SOKETI_HOST: process.env.NEXT_PUBLIC_SOKETI_HOST,
    NEXT_PUBLIC_SOKETI_PORT: process.env.NEXT_PUBLIC_SOKETI_PORT,
    NEXT_PUBLIC_SOKETI_USE_TLS: process.env.NEXT_PUBLIC_SOKETI_USE_TLS,
    NEXT_PUBLIC_CLARITY_ID: process.env.NEXT_PUBLIC_CLARITY_ID,
    // NEXT_PUBLIC_CLIENTVAR: process.env.NEXT_PUBLIC_CLIENTVAR,
  },
  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
   * useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  /**
   * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
   * `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
});
